import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input } from 'reactstrap';

export const AdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [newUser, setNewUser] = useState({ username: '', email: '', isAdmin: false });
    const [error, setError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        checkAdminAccess();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only admin check

    const checkAdminAccess = async () => {
        try {
            const response = await fetch('api/identity/getuser');
            const data = await response.json();
            if (data.roles && data.roles.some(r => r.toLowerCase() === 'admin')) {
                setIsAdmin(true);
                loadUsers();
            } else {
                navigate('/');
            }
        } catch (err) {
            navigate('/');
        }
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            const response = await fetch('api/identity/admin/users');
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            } else {
                setError('Failed to load users.');
            }
        } catch (err) {
            setError('Failed to load users.');
        }
        setLoading(false);
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setError('');

        if (!newUser.username.trim()) {
            setError('Username is required.');
            return;
        }

        try {
            const response = await fetch('api/identity/admin/users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newUser)
            });
            const data = await response.json();

            if (data.success) {
                setShowAddModal(false);
                setNewUser({ username: '', email: '', isAdmin: false });
                setCredentials({ username: data.username, password: data.generatedPassword });
                setShowCredentialsModal(true);
                loadUsers();
            } else {
                setError(data.message || 'Failed to create user.');
            }
        } catch (err) {
            setError('Failed to create user.');
        }
    };

    const handleDeleteUser = async (username) => {
        if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await fetch(`api/identity/admin/users/${encodeURIComponent(username)}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                loadUsers();
            } else {
                const text = await response.text();
                alert(text || 'Failed to delete user.');
            }
        } catch (err) {
            alert('Failed to delete user.');
        }
    };

    const handleResetPassword = async (username) => {
        if (!window.confirm(`Are you sure you want to reset the password for "${username}"?`)) {
            return;
        }

        try {
            const response = await fetch(`api/identity/admin/users/${encodeURIComponent(username)}/reset-password`, {
                method: 'POST'
            });
            const data = await response.json();

            if (data.success) {
                setCredentials({ username: username, password: data.newPassword });
                setShowCredentialsModal(true);
            } else {
                alert(data.message || 'Failed to reset password.');
            }
        } catch (err) {
            alert('Failed to reset password.');
        }
    };

    const handleToggleAdmin = async (username, currentIsAdmin) => {
        try {
            const response = await fetch(`api/identity/admin/users/${encodeURIComponent(username)}/toggle-admin`, {
                method: 'POST'
            });

            if (response.ok) {
                loadUsers();
            } else {
                const text = await response.text();
                alert(text || 'Failed to update admin status.');
            }
        } catch (err) {
            alert('Failed to update admin status.');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('Copied to clipboard!');
        }).catch(() => {
            alert('Failed to copy to clipboard.');
        });
    };

    if (!isAdmin) {
        return <div className="container" style={{ marginTop: 10 }}>Checking access...</div>;
    }

    return (
        <div className="container" style={{ marginTop: 10 }}>
            <div className="row">
                <div className="col-12">
                    <h3>User Management</h3>
                    <p className="text-muted">Manage users in your organization. You can add new users, reset passwords, and manage admin privileges.</p>

                    <Button color="primary" onClick={() => setShowAddModal(true)} style={{ marginBottom: 15 }}>
                        Add User
                    </Button>

                    {error && <div className="alert alert-danger">{error}</div>}

                    {loading ? (
                        <p>Loading users...</p>
                    ) : users.length === 0 ? (
                        <p>No other users found in your organization.</p>
                    ) : (
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th>Username</th>
                                    <th>Email</th>
                                    <th>Admin</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(user => (
                                    <tr key={user.username}>
                                        <td>{user.username}</td>
                                        <td>{user.email || '-'}</td>
                                        <td>
                                            <Input
                                                type="checkbox"
                                                checked={user.isAdmin}
                                                onChange={() => handleToggleAdmin(user.username, user.isAdmin)}
                                                style={{ marginLeft: 0, position: 'relative' }}
                                            />
                                        </td>
                                        <td>
                                            <Button
                                                color="warning"
                                                size="sm"
                                                onClick={() => handleResetPassword(user.username)}
                                                style={{ marginRight: 5 }}
                                            >
                                                Reset Password
                                            </Button>
                                            <Button
                                                color="danger"
                                                size="sm"
                                                onClick={() => handleDeleteUser(user.username)}
                                            >
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Add User Modal */}
            <Modal isOpen={showAddModal} toggle={() => setShowAddModal(false)}>
                <ModalHeader toggle={() => setShowAddModal(false)}>Add New User</ModalHeader>
                <Form onSubmit={handleAddUser}>
                    <ModalBody>
                        {error && <div className="alert alert-danger">{error}</div>}
                        <FormGroup>
                            <Label for="username">Username *</Label>
                            <Input
                                type="text"
                                id="username"
                                value={newUser.username}
                                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                placeholder="Enter username"
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="email">Email</Label>
                            <Input
                                type="email"
                                id="email"
                                value={newUser.email}
                                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                placeholder="Enter email (optional)"
                            />
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="checkbox"
                                    checked={newUser.isAdmin}
                                    onChange={(e) => setNewUser({ ...newUser, isAdmin: e.target.checked })}
                                />{' '}
                                Make this user an admin
                            </Label>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
                        <Button color="primary" type="submit">Create User</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Credentials Modal */}
            <Modal isOpen={showCredentialsModal} toggle={() => setShowCredentialsModal(false)}>
                <ModalHeader toggle={() => setShowCredentialsModal(false)}>User Credentials</ModalHeader>
                <ModalBody>
                    <div className="alert alert-warning">
                        <strong>Important:</strong> Please save these credentials now. The password will not be shown again.
                    </div>
                    <FormGroup>
                        <Label><strong>Username:</strong></Label>
                        <div className="input-group">
                            <Input type="text" value={credentials.username} readOnly />
                            <Button color="secondary" onClick={() => copyToClipboard(credentials.username)}>Copy</Button>
                        </div>
                    </FormGroup>
                    <FormGroup>
                        <Label><strong>Password:</strong></Label>
                        <div className="input-group">
                            <Input type="text" value={credentials.password} readOnly />
                            <Button color="secondary" onClick={() => copyToClipboard(credentials.password)}>Copy</Button>
                        </div>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setShowCredentialsModal(false)}>Done</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};
